/**
 * @file render-helpers.ts
 * @description Provides auxiliary drawing functions for canvas overlays (Crop UI, Brush Preview, Selections).
 */

import { g, Tool } from '@hcie/core';
import { drawSelectionBorder } from '@hcie/tools';
import { EventBus } from '@hcie/shared';
import { getActiveLayer } from '../layer-service';

let toolWarningText: string = "";
let toolWarningTimeout: any = null;

/**
 * @function setToolWarning
 * @description Sets a temporary warning message to be displayed near the cursor.
 * @param {string} text - The warning message.
 */
export function setToolWarning(text: string): void {
    toolWarningText = text;
    if (toolWarningTimeout) clearTimeout(toolWarningTimeout);
    toolWarningTimeout = setTimeout(() => {
        toolWarningText = "";
    }, 2000);
}

/**
 * @function drawMarchingAnts
 * @description Proxies the selection border drawing from the tools package.
 * @param {CanvasRenderingContext2D} ctx - The target canvas context.
 */
export function drawMarchingAnts(ctx: CanvasRenderingContext2D): void {
    // [CRITICAL_LOGIC_START]
    const fc = (g as any).floatingContent || (window as any).floatingContent;
    
    // RULE: When transform is active, the SelectionTransformUI overlay handles all visuals.
    // Suppress marching ants and ghost handles to avoid double rendering.
    if (fc) return;

    const isPolyOrLassoTool: boolean =
        g.current_tool?.id === Tool.PolySelect?.id ||
        g.current_tool?.id === Tool.Lasso?.id;

    // Draw marching ants during active selection OR during PolySelect/Lasso drawing phase
    if (g.isSelectionActive || (g.drawing && isPolyOrLassoTool)) {
        drawSelectionBorder(ctx);
    }
    // [CRITICAL_LOGIC_END]
}

/**
 * @function drawCropUI
 * @description Renders the cropping overlay, boundary, and handles for pending crop actions.
 * @param {CanvasRenderingContext2D} ctx - The target canvas context.
 */
export function drawCropUI(ctx: CanvasRenderingContext2D): void {
    // [CRITICAL_LOGIC_START]
    const cropRect = (g as any).lastCropRect;
    if (!cropRect || cropRect.w < 5 || cropRect.h < 5) {
        return;
    }

    ctx.save();
    
    // STEP 1: Dimmed Background Overlay
    ctx.fillStyle = "rgba(0, 0, 0, 0.45)";
    ctx.fillRect(0, 0, g.image_width, cropRect.y);
    ctx.fillRect(0, cropRect.y + cropRect.h, g.image_width, g.image_height - (cropRect.y + cropRect.h));
    ctx.fillRect(0, cropRect.y, cropRect.x, cropRect.h);
    ctx.fillRect(cropRect.x + cropRect.w, cropRect.y, g.image_width - (cropRect.x + cropRect.w), cropRect.h);

    // STEP 2: Main Crop Border
    ctx.strokeStyle = "rgba(0, 120, 215, 1.0)";
    ctx.lineWidth = 1.5;
    ctx.strokeRect(cropRect.x, cropRect.y, cropRect.w, cropRect.h);

    // STEP 3: Control Handles
    const handleSize = 7;
    const handles = [
        { x: cropRect.x, y: cropRect.y }, { x: cropRect.x + cropRect.w / 2, y: cropRect.y }, { x: cropRect.x + cropRect.w, y: cropRect.y },
        { x: cropRect.x, y: cropRect.y + cropRect.h / 2 }, { x: cropRect.x + cropRect.w, y: cropRect.y + cropRect.h / 2 },
        { x: cropRect.x, y: cropRect.y + cropRect.h }, { x: cropRect.x + cropRect.w / 2, y: cropRect.y + cropRect.h }, { x: cropRect.x + cropRect.w, y: cropRect.y + cropRect.h }
    ];
    
    handles.forEach(handle => {
        ctx.fillStyle = "#ffffff";
        ctx.strokeStyle = "#0078d7";
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.rect(handle.x - handleSize/2, handle.y - handleSize/2, handleSize, handleSize);
        ctx.fill();
        ctx.stroke();
    });
    
    // STEP 4: Helper Instructions
    if (cropRect.w > 80 && cropRect.h > 40) {
        ctx.fillStyle = "white";
        ctx.font = "bold 11px Arial, sans-serif";
        ctx.textAlign = "center";
        ctx.shadowColor = "black";
        ctx.shadowBlur = 4;
        ctx.fillText("ENTER ONAYLAR", cropRect.x + cropRect.w/2, cropRect.y + cropRect.h/2);
        ctx.fillText("ESC İPTAL", cropRect.x + cropRect.w/2, cropRect.y + cropRect.h/2 + 14);
    }

    ctx.restore();
    // [CRITICAL_LOGIC_END]
}

/**
 * @function drawBrushPreviewCircle
 * @description Renders a circular cursor preview for the current brush size on the canvas.
 * @param {CanvasRenderingContext2D} ctx - The target canvas context.
 */
export function drawBrushPreviewCircle(ctx: CanvasRenderingContext2D): void {
    const drawingToolIds: readonly string[] = [
        Tool.Pen.id, 
        Tool.Brush.id, 
        Tool.Eraser.id, 
        Tool.Spray.id, 
        'btn-red-eye'
    ];
    
    const isDrawingTool: boolean = drawingToolIds.includes(g.current_tool?.id);
    const isInteractionActive: boolean = g.zooming || g.movingSelection;
    const activeLayer = getActiveLayer();
    const isVectorLayer = activeLayer?.type === 'vector';

    if (isDrawingTool && !isInteractionActive && !isVectorLayer) {
        ctx.save();
        ctx.beginPath();
        
        const radius: number = Math.max(2, g.pen_width / 2);
        ctx.arc(g.pX, g.pY, radius, 0, Math.PI * 2);
        
        ctx.globalAlpha = 0.8;
        
        // Outer contrast stroke
        ctx.lineWidth = 2.5;
        ctx.strokeStyle = '#000000';
        ctx.stroke();
        
        // Inner white stroke
        ctx.lineWidth = 1;
        ctx.strokeStyle = '#ffffff';
        ctx.stroke();
        
        ctx.restore();
    }
}

/**
 * @function drawFloatingWarning
 * @description Renders a temporary warning message near the mouse cursor.
 * @param {CanvasRenderingContext2D} ctx - The target canvas context.
 */
export function drawFloatingWarning(ctx: CanvasRenderingContext2D): void {
    if (!toolWarningText) return;

    ctx.save();
    
    // Position slightly offset from cursor
    const x = g.pX + 15;
    const y = g.pY - 15;

    ctx.font = "bold 13px Roboto, sans-serif";
    const metrics = ctx.measureText(toolWarningText);
    const padding = 6;
    const w = metrics.width + padding * 2;
    const h = 22;

    // Drawing background bubble
    ctx.fillStyle = "rgba(255, 50, 50, 0.9)";
    ctx.strokeStyle = "#ffffff";
    ctx.lineWidth = 1.5;
    
    // Simple rounded rect
    const r = 5;
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + r);
    ctx.lineTo(x + w, y + h - r);
    ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    ctx.lineTo(x + r, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - r);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.closePath();
    
    ctx.fill();
    ctx.stroke();

    // Drawing shadow-ish text
    ctx.fillStyle = "#ffffff";
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    ctx.fillText(toolWarningText, x + padding, y + h/2);

    ctx.restore();
}
